import { z } from 'zod';
import { Id, Role } from '../primitives.js';
import { MembershipStatus } from './identity.js';

/**
 * Справочник сотрудников клиники: кто здесь работает и как его зовут.
 *
 * Это **проекция членства**, а не новая сущность. Человек и его роль уже живут
 * в `identity.user` и `identity.membership`; здесь к ним добавлены два поля,
 * которых системе не хватало для бумаги, — должность и специальность.
 *
 * Зачем это понадобилось раньше своей фазы. Под планом лечения, договором и
 * справкой стоит подпись врача с расшифровкой: «Лечащий врач ______ Верещагин
 * И. Д.». Расшифровку неоткуда взять, пока имя сотрудника есть только в
 * учётной записи того, кто вошёл: план мог составить один врач, а печатает
 * бумагу администратор. Поэтому имена всех сотрудников клиники лежат на
 * устройстве рядом с планами.
 *
 * Чего здесь нет и не будет до фазы 3: оклада, ставки, KPI и мотивации. Они —
 * в `StaffProfile` модуля «Персонал», и на устройство они не попадают вовсе.
 * Разница не в объёме полей, а в том, что этот справочник читают все
 * сотрудники клиники, а зарплату — двое.
 *
 * Документация модуля: docs/03-modules/16-staff.md
 */

/**
 * Должность так, как она печатается в документе: «врач-стоматолог-терапевт».
 * Это не роль в системе: роль отвечает на вопрос «что ему можно», должность —
 * «как его называть в бумаге», и совпадать они не обязаны.
 */
const jobTitle = z.string().trim().min(3).max(120);

/**
 * Специальность: «терапевтическая стоматология», «ортодонтия». Нужна не бумаге,
 * а расписанию — по ней регистратура выбирает, к кому записать пациента.
 */
const specialty = z.string().trim().min(3).max(120);

export const StaffMember = z.object({
  /** Идентификатор членства: человек в двух клиниках — две записи справочника. */
  id: Id,
  tenantId: Id,
  /** Тот же идентификатор, что стоит в `createdBy` плана и в `doctorId` смены. */
  userId: Id,
  role: Role,
  status: MembershipStatus,
  lastName: z.string().min(1).max(100),
  firstName: z.string().min(1).max(100),
  middleName: z.string().max(100).nullable(),
  jobTitle: jobTitle.nullable(),
  specialty: specialty.nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type StaffMember = z.infer<typeof StaffMember>;

/**
 * Правка карточки сотрудника.
 *
 * Роли здесь нет намеренно. Смена роли — не правка справочника, а изменение
 * прав: она отзывает сессии, требует, чтобы в клинике остался владелец, и
 * пишется в журнал безопасности отдельной строкой. Смешать её с правкой
 * должности значило бы дать сменить права незаметно для того, кто смотрит
 * журнал доступа.
 */
export const UpdateStaffMemberRequest = z.object({
  jobTitle: jobTitle.nullable().optional(),
  specialty: specialty.nullable().optional(),
});
export type UpdateStaffMemberRequest = z.infer<typeof UpdateStaffMemberRequest>;

export const StaffListResponse = z.object({
  members: z.array(StaffMember),
});
export type StaffListResponse = z.infer<typeof StaffListResponse>;

/**
 * Работает ли сотрудник прямо сейчас.
 *
 * Приглашённый уже виден в справочнике — его должность заполняют до первого
 * входа, — но врачом в расписании он ещё не является. Отстранённый и уволенный
 * остаются в справочнике навсегда: под документами прошлого года стоят их
 * подписи, и расшифровка обязана находиться и через пять лет.
 */
export function isWorking(member: StaffMember): boolean {
  return member.status === 'active';
}

/** Календарный месяц начисления и целей: `2026-08`. */
export const StaffPeriod = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
export type StaffPeriod = z.infer<typeof StaffPeriod>;

export const ABSENCE_KIND = ['vacation', 'sick', 'day_off'] as const;
export const AbsenceKind = z.enum(ABSENCE_KIND);
export type AbsenceKind = z.infer<typeof AbsenceKind>;

export const ABSENCE_STATUS = ['draft', 'submitted', 'approved', 'rejected', 'cancelled'] as const;
export const AbsenceStatus = z.enum(ABSENCE_STATUS);
export type AbsenceStatus = z.infer<typeof AbsenceStatus>;

export const STAFF_PERIOD_STATUS = ['open', 'calculated', 'closed'] as const;
export const StaffPeriodStatus = z.enum(STAFF_PERIOD_STATUS);
export type StaffPeriodStatus = z.infer<typeof StaffPeriodStatus>;

export const PAYROLL_ADJUSTMENT_KIND = ['bonus', 'demerit'] as const;
export const PayrollAdjustmentKind = z.enum(PAYROLL_ADJUSTMENT_KIND);
export type PayrollAdjustmentKind = z.infer<typeof PayrollAdjustmentKind>;

export const KPI_CODE = [
  'appointments_completed',
  'no_show_rate',
  'plan_conversion',
  'tasks_on_time',
  'bookings_created',
  'revenue',
  'average_check',
  'phone_conversion',
] as const;
export const KpiCode = z.enum(KPI_CODE);
export type KpiCode = z.infer<typeof KpiCode>;

export const KPI_SCOPE = ['clinic', 'doctor', 'receptionist', 'head_nurse'] as const;
export const KpiScope = z.enum(KPI_SCOPE);
export type KpiScope = z.infer<typeof KpiScope>;

/**
 * Профиль фазы 3. Денежные поля на устройство не попадают: ставка и оклад
 * читаются только online и только владельцем/бухгалтером.
 */
export const StaffProfile = z.object({
  membershipId: Id,
  startedAt: z.iso.date().nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type StaffProfile = z.infer<typeof StaffProfile>;

export const UpdateStaffProfileRequest = z.object({
  startedAt: z.iso.date().nullable().optional(),
  /** Ставка в копейках. Пишется только владельцем, шифруется на сервере. */
  rateKopecks: z.number().int().nonnegative().nullable().optional(),
});
export type UpdateStaffProfileRequest = z.infer<typeof UpdateStaffProfileRequest>;

export const MotivationScheme = z.object({
  membershipId: Id,
  /** Сотые процента выручки: 1500 = 15%. */
  percentBps: z.number().int().min(0).max(10_000),
  kpiWeight: z.number().int().min(0).max(100),
  baseVariableKopecks: z.number().int().nonnegative(),
  /** Виден только владельцу и бухгалтеру — сервер проверяет это до ответа, не после. */
  salaryKopecks: z.number().int().nonnegative().nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type MotivationScheme = z.infer<typeof MotivationScheme>;

export const UpsertMotivationSchemeRequest = z.object({
  percentBps: z.number().int().min(0).max(10_000),
  kpiWeight: z.number().int().min(0).max(100),
  baseVariableKopecks: z.number().int().nonnegative().default(0),
  salaryKopecks: z.number().int().nonnegative().nullable().optional(),
});
export type UpsertMotivationSchemeRequest = z.infer<typeof UpsertMotivationSchemeRequest>;

export const KpiDefinition = z.object({
  id: Id,
  code: KpiCode,
  name: z.string().min(1).max(120),
  scope: KpiScope,
  target: z.number().nonnegative(),
  weight: z.number().int().min(0).max(100),
  period: StaffPeriod,
  role: Role.nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type KpiDefinition = z.infer<typeof KpiDefinition>;

export const CreateKpiDefinitionRequest = z.object({
  code: KpiCode,
  name: z.string().min(1).max(120),
  scope: KpiScope,
  target: z.number().nonnegative(),
  weight: z.number().int().min(0).max(100),
  period: StaffPeriod,
  role: Role.nullable().default(null),
});
export type CreateKpiDefinitionRequest = z.infer<typeof CreateKpiDefinitionRequest>;

export const UpdateKpiDefinitionRequest = z.object({
  name: z.string().min(1).max(120).optional(),
  target: z.number().nonnegative().optional(),
  weight: z.number().int().min(0).max(100).optional(),
});
export type UpdateKpiDefinitionRequest = z.infer<typeof UpdateKpiDefinitionRequest>;

export const KpiResult = z.object({
  membershipId: Id,
  definitionId: Id,
  code: KpiCode,
  period: StaffPeriod,
  actual: z.number().nullable(),
  target: z.number().nonnegative(),
  attainment: z.number().nullable(),
  available: z.boolean(),
  weight: z.number().int().min(0).max(100),
});
export type KpiResult = z.infer<typeof KpiResult>;

export const Target = z.object({
  id: Id,
  period: StaffPeriod,
  scope: KpiScope,
  membershipId: Id.nullable(),
  code: KpiCode,
  value: z.number().nonnegative(),
  rowVersion: z.number().int().nonnegative(),
});
export type Target = z.infer<typeof Target>;

export const UpsertTargetRequest = z.object({
  period: StaffPeriod,
  scope: KpiScope,
  membershipId: Id.nullable().default(null),
  code: KpiCode,
  value: z.number().nonnegative(),
});
export type UpsertTargetRequest = z.infer<typeof UpsertTargetRequest>;

export const Absence = z.object({
  id: Id,
  membershipId: Id,
  kind: AbsenceKind,
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  status: AbsenceStatus,
  rejectReason: z.string().max(500).nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type Absence = z.infer<typeof Absence>;

export const CreateAbsenceRequest = z.object({
  membershipId: Id.optional(),
  kind: AbsenceKind,
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
});
export type CreateAbsenceRequest = z.infer<typeof CreateAbsenceRequest>;

export const AbsenceChange = z.discriminatedUnion('action', [
  z.object({ action: z.literal('submit') }),
  z.object({ action: z.literal('cancel') }),
]);
export type AbsenceChange = z.infer<typeof AbsenceChange>;

export const ApproveAbsenceRequest = z.object({
  decision: z.enum(['approve', 'reject']),
  reason: z.string().min(3).max(500).optional(),
});
export type ApproveAbsenceRequest = z.infer<typeof ApproveAbsenceRequest>;

export const PayrollAdjustment = z.object({
  id: Id,
  membershipId: Id,
  period: StaffPeriod,
  kind: PayrollAdjustmentKind,
  amountKopecks: z.number().int(),
  reason: z.string().min(3).max(1000),
  createdBy: Id,
  createdAt: z.iso.datetime(),
});
export type PayrollAdjustment = z.infer<typeof PayrollAdjustment>;

export const CreatePayrollAdjustmentRequest = z.object({
  membershipId: Id,
  period: StaffPeriod,
  kind: PayrollAdjustmentKind,
  amountKopecks: z.number().int().positive(),
  reason: z.string().min(3).max(1000),
});
export type CreatePayrollAdjustmentRequest = z.infer<typeof CreatePayrollAdjustmentRequest>;

export const VariablePayView = z.object({
  membershipId: Id,
  period: StaffPeriod,
  status: StaffPeriodStatus,
  /** Сотые процента (1500 — это 15%), ставка схемы на момент расчёта. */
  percentBps: z.number().int().nonnegative(),
  fromPercentKopecks: z.number().int(),
  fromBaseKopecks: z.number().int(),
  fromKpiKopecks: z.number().int(),
  adjustmentsKopecks: z.number().int(),
  totalKopecks: z.number().int(),
  score: z.number().nullable(),
  results: z.array(KpiResult),
  /**
   * Премии и депремирование поштучно, от старой к свежей.
   *
   * Одной суммой обойтись нельзя: премия обязана иметь основание
   * (docs/03-modules/16-staff.md, §6), а основание, которое ввели и больше
   * никогда не показали, — это не основание. Сотрудник видит, за что ему
   * начислено, руководитель — что именно он подписал.
   */
  adjustments: z.array(PayrollAdjustment),
});
export type VariablePayView = z.infer<typeof VariablePayView>;

export const PeriodClosure = z.object({
  period: StaffPeriod,
  status: StaffPeriodStatus,
  closedBy: Id.nullable(),
  closedAt: z.iso.datetime().nullable(),
});
export type PeriodClosure = z.infer<typeof PeriodClosure>;

